import React, {useMemo, useState} from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import {Add, Edit, Delete} from "@mui/icons-material";
import {withAuthenticationRequired} from "@auth0/auth0-react";
import Loading from "../components/common/Loading";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@apollo/client";
import {DELETE_STAY, FETCH_STAYS, SAVE_OR_UPDATE_STAY} from "../gql/stays.gql";
import AppSnackbar from "../common/AppSnackbar";
import AlertDialogSlide from "../components/common/AlertDialogSlide";

const StayConfiguration = () => {
    const {data: fetchStayData} = useQuery(FETCH_STAYS);
    const [saveOrUpdateStay] = useMutation(SAVE_OR_UPDATE_STAY)
    const [deleteStay] = useMutation(DELETE_STAY)
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editingStay, setEditingStay] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [severity, setSeverity] = useState("success");
    const STAY_TYPES = ["XL", "STANDARD", "CAGE", "OUTDOOR"];
    const [numberToBulkCreate, setNumberToBulkCreate] = useState(1);
    // Form
    const [form, setForm] = useState({
        name: "",
        description: "",
        stayType: "",
        number: "",
        price: "",
        numberOfGuests: "",
        location: "",
    });

    // ---------- Dialog handlers ----------
    const handleOpen = (stay) => {
        if (stay) {
            setEditingStay(stay);
            setForm({
                id: stay.id,
                name: stay.name ?? "",
                description: stay.description ?? "",
                stayType: stay.stayType ?? "",
                number: stay.number ?? "",
                price: stay.price ?? "",
                numberOfGuests: stay.numberOfGuests ?? "",
                location: stay.location ?? "",
            });
        } else {
            setEditingStay(null);
            setForm({
                name: "",
                description: "",
                stayType: "",
                price: "",
                number: "",
                numberOfGuests: "",
                location: "",
            });
        }
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const onChange = (e) => {
        const {name, value} = e.target;
        setForm((prev) => ({...prev, [name]: value}));
    };

    const isValid = useMemo(() => {
        if (!form.name?.trim()) return false;
        if (!form.stayType) return false;
        if (!form.location) return false;
        if (!form.price) return false;
        if (form.number !== "" && Number(form.number) < 0) return false;
        if (form.numberOfGuests !== "" && Number(form.numberOfGuests) < 0) return false;
        return true;
    }, [form]);

    // ---------- Save / Delete ----------
    // Alert state for confirmations
    const [openAlert, setOpenAlert] = useState(false);
    const [alertTitle, setAlertTitle] = useState("");
    const [alertMessage, setAlertMessage] = useState("");
    const [alertAction, setAlertAction] = useState("");
    const [pendingId, setPendingId] = useState(null);

    const resetAlert = () => {
        setOpenAlert(false);
        setAlertTitle("");
        setAlertMessage("");
        setAlertAction("");
        setPendingId(null);
    };
    const handleSave = async () => {
        if (!isValid) {
            setSnackbarMessage('fillAllRequiredFields');
            setSeverity("warning");
            setOpenSnackbar(true);
            return;
        }
        const payload = {
            ...form,
            number: Number(form.number || 0),
            numberOfGuests: Number(form.numberOfGuests || 0),
        };
        try {
            saveOrUpdateStay({
                variables: {input: payload, aantal: numberToBulkCreate},
                refetchQueries: [
                    {query: FETCH_STAYS}
                ],
            }).then(res => {

            })
                .catch(err => {
                    console.error("Error adding attention", err);
                })

            handleClose();
        } catch (e) {
            setSnackbarMessage("Opslaan mislukt");
            setSeverity("error");
            setOpenSnackbar(true);
        }
    };

    const handleDelete = async (id) => {
        setPendingId(id);
        setAlertTitle(t('confirm'));
        setAlertMessage('areYouSureYouWantToRemoveThisStay');
        setAlertAction('DELETE_STAY');
        setOpenAlert(true);
    };

    const handleCloseAlert = async (result, action) => {
        setOpenAlert(false);
        if (result && action === 'DELETE_STAY' && pendingId != null) {
            try {
                await deleteStay({
                    variables: {id: pendingId},
                    refetchQueries: [
                        {query: FETCH_STAYS}
                    ],
                });
                setSnackbarMessage('stayRemoved');
                setSeverity("success");
                setOpenSnackbar(true);
            } catch (e) {
                setSnackbarMessage('removeFailed');
                setSeverity("error");
                setOpenSnackbar(true);
            } finally {
                resetAlert();
            }
        } else {
            resetAlert();
        }
    }

    const stays = fetchStayData?.stays ?? [];

    return (
        <Box sx={{width: '100%', display: 'block', textAlign: 'left'}}>
            <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{mb: 3, width: '100%'}}
                wrap="nowrap"
            >
                <Grid item>
                    <Typography variant="h4" component="h2">
                        {t('staysConfig')}
                    </Typography>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        startIcon={<Add/>}
                        sx={{minWidth: 220}}
                        onClick={() => handleOpen(null)}
                        data-testid="add-new-stay-button"
                    >
                        {t('newStay')}
                    </Button>
                </Grid>
            </Grid>

            {/* Tabel + placeholder */}
            <Paper variant="outlined" sx={{p: 0}}>
                {loading ? (
                    <Box sx={{height: 260, display: "grid", placeItems: "center"}}>
                        <CircularProgress/>
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('location')}</TableCell>
                                <TableCell>{t('name')}</TableCell>
                                <TableCell>{t('description')}</TableCell>
                                <TableCell>{t('type')}</TableCell>
                                <TableCell>{t('price')}</TableCell>
                                <TableCell align="right">{t('stayNumber')}</TableCell>
                                <TableCell align="right">{t('guestsPerStay')}</TableCell>
                                <TableCell align="center">{t('actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stays.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Box sx={{py: 6, textAlign: "center"}}>
                                            <Typography color="text.secondary">
                                                Nog geen stays. Klik op <b>Nieuwe Stay</b> om te beginnen.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                stays.map((stay) => (
                                    <TableRow key={stay.id} hover>
                                        <TableCell>{stay.location}</TableCell>
                                        <TableCell>{stay.name}</TableCell>
                                        <TableCell>{stay.description}</TableCell>
                                        <TableCell>{stay.stayType}</TableCell>
                                        <TableCell>{stay.price}</TableCell>
                                        <TableCell align="right">{stay.number}</TableCell>
                                        <TableCell align="right">{stay.numberOfGuests}</TableCell>
                                        <TableCell align="center" sx={{whiteSpace: "nowrap"}}>
                                            <IconButton onClick={() => handleOpen(stay)} color="primary" data-testid={`edit-stay-button-${stay.name}`}>
                                                <Edit/>
                                            </IconButton>
                                            <IconButton onClick={() => handleDelete(stay.id)} color="error" data-testid={`delete-stay-button-${stay.name}`}>
                                                <Delete/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            {/* Dialoog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>{editingStay ? t('editStay') : t('newStay')}</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="normal"
                        label={t('location')}
                        name="location"
                        value={form.location}
                        onChange={onChange}
                        fullWidth
                        required
                        inputProps={{maxLength: 50}}
                    />
                    <TextField
                        margin="normal"
                        label={t('name')}
                        name="name"
                        value={form.name}
                        onChange={onChange}
                        fullWidth
                        required
                        inputProps={{maxLength: 80}}
                    />
                    <TextField
                        margin="normal"
                        label={t('description')}
                        name="description"
                        value={form.description}
                        onChange={onChange}
                        fullWidth
                        multiline
                        inputProps={{maxLength: 300}}
                    />
                    <TextField
                        select
                        margin="normal"
                        label={t('type')}
                        name="stayType"
                        value={form.stayType}
                        onChange={onChange}
                        fullWidth
                        required
                    >
                        {STAY_TYPES.map((type) => (
                            <MenuItem key={type} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Grid container spacing={2} sx={{mt: 0}}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                margin="normal"
                                type="number"
                                label={t('stayNumber')}
                                name="number"
                                value={form.number}
                                onChange={onChange}
                                fullWidth
                                inputProps={{min: 1}}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                margin="normal"
                                type="number"
                                label={t('price')}
                                name="price"
                                value={form.price}
                                onChange={onChange}
                                fullWidth
                                inputProps={{min: 1}}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                margin="normal"
                                type="number"
                                label={t('guestsPerStay')}
                                name="numberOfGuests"
                                value={form.numberOfGuests}
                                onChange={onChange}
                                fullWidth
                                inputProps={{min: 1}}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{mt: 0}}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                margin="normal"
                                type="number"
                                label={t('amountToCreate')}
                                name="number"
                                value={numberToBulkCreate}
                                onChange={(e) => setNumberToBulkCreate(Number(e.target.value))}
                                fullWidth
                                inputProps={{min: 1}}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>{t('cancel')}</Button>
                    <Button variant="contained" onClick={handleSave} disabled={!isValid}>
                        {t('save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <AppSnackbar
                openSnackbar={openSnackbar}
                snackbarMessage={snackbarMessage}
                setOpenSnackbar={setOpenSnackbar}
                severity={severity}
            />

            <AlertDialogSlide
                title={alertTitle}
                message={alertMessage}
                openAlert={openAlert}
                action={alertAction}
                onClose={handleCloseAlert}
            />
        </Box>
    );
};

export default withAuthenticationRequired(StayConfiguration, {
    onRedirecting: () => <Loading/>,
    returnTo: "/beheer/login",
});
